import { toEnDigits } from './utils';

/**
 * «شمارهٔ پیگیری تکراری» (PM-0711) — one place for the rule.
 *
 * The same bank transfer was recorded twice for one patient, once as the invoice's
 * payment and once as an appointment deposit, and nothing said so: the account then
 * claimed money that arrived once. These are the pure pieces that decide whether a
 * reference being typed has already been recorded for this patient.
 *
 * It WARNS and never refuses. A single transfer can honestly be split across several
 * entries (two invoices, an invoice and a deposit), and the desk is the one who knows;
 * a server refusal with no way past it would block that work. The server-side refusal
 * needs a new five-language sentence in the kit's shared server-text catalogue and is
 * tracked separately.
 */

// Only a BANK TRANSFER's tracking number names ONE transfer. A POS slip number is the
// terminal's own and repeats across terminals and days; a «third party» reference is
// WHO paid (an employer pays again and again under the same name); cash has none.
const CHECKED_METHODS = new Set(['BANK_TRANSFER']);

/** A number shorter than this is half-typed, and matching it would warn on every keystroke. */
export const MIN_REFERENCE_LENGTH = 6;

// The statuses of a payment whose money is NOT on the books: reversed, never arrived, abandoned.
const DEAD_STATUSES = new Set(['VOID', 'FAILED', 'CANCELLED']);

export const isCheckedMethod = (method) => CHECKED_METHODS.has(String(method || '').toUpperCase());

/**
 * A reference as comparable text: Persian / Arabic-Indic digits read as Latin, upper-cased,
 * and the spaces, dashes, dots and slashes a person types around it dropped. «۰۴۰۳-۳۰۵۶ ۱۲۹۵»
 * and «040330561295» are the same transfer.
 */
export const normalizeReference = (value) => toEnDigits(String(value ?? ''))
  .toUpperCase()
  .replace(/[\s\-_./\\]+/g, '');

/**
 * A deposit has no reference column: the BFF writes «reference · note» into its note
 * (`recordAppointmentDeposit`, and the drawer's «پیش‌دریافت» before it), so the
 * reference is the part before the first « · ».
 */
export const depositReferenceOf = (row) => String(row?.note || '').split(' · ')[0];

/**
 * The first recorded payment or deposit of this patient that carries the same bank
 * transfer reference, or null.
 *
 * @param {object}   args
 * @param {string}   args.method     the method being entered (any casing)
 * @param {string}   args.reference  what the desk typed
 * @param {object[]} args.payments   the patient's payments (AccountingPayment)
 * @param {object[]} args.entries    the patient's appointment deposits (PatientDepositEntry)
 * @returns {{kind:'payment'|'deposit', amountRial:number, at:string, currency:string, appointmentId?:string}|null}
 */
export function findDuplicateReference({ method, reference, payments, entries }) {
  if (!isCheckedMethod(method)) return null;
  const wanted = normalizeReference(reference);
  if (wanted.length < MIN_REFERENCE_LENGTH) return null;

  for (const p of payments || []) {
    if (String(p.kind || 'payment').toLowerCase() !== 'payment') continue;       // a refund is money going back
    if (DEAD_STATUSES.has(String(p.status || '').toUpperCase())) continue;
    if (!isCheckedMethod(p.paymentMethod)) continue;
    if (normalizeReference(p.referenceNumber) === wanted) {
      return { kind: 'payment', amountRial: Number(p.amount) || 0, at: p.createdAt, currency: p.currency };
    }
  }
  for (const entry of entries || []) {
    for (const row of entry?.deposit?.rows || []) {
      if (row.source !== 'deposit') continue;                                    // applied / refund rows are not an entry of money
      if (!isCheckedMethod(row.paymentMethod)) continue;
      if (normalizeReference(depositReferenceOf(row)) === wanted) {
        return {
          kind: 'deposit', amountRial: Number(row.amountRial) || 0, at: row.createdAt,
          currency: row.currency, appointmentId: entry.deposit.appointmentId,
        };
      }
    }
  }
  return null;
}
